import {
  displayServerMask,
  formatRatePercent,
  MASK_CHAR,
  toLocalMobile,
  toPersianDigits,
} from '@saay/formatting';

import type { TargetingFilter } from './api.js';

/** Program or policy revision for display (`نسخه ۲`, not `v2` which reads as ۷۱ in Persian fonts). */
export function formatRevisionLabel(revision: number): string {
  return `نسخه ${toPersianDigits(revision)}`;
}

export function formatProgramCodeRevision(programCode: string, revision: number): string {
  return `${programCode} / ${formatRevisionLabel(revision)}`;
}

/** Compact support reference; the full value remains available to copy. */
export function shortReference(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length <= 12) return trimmed;
  return `${trimmed.slice(0, 8)}…${trimmed.slice(-4)}`;
}

/** Server-masked card or account number with • and 4-digit groups (FTD §3.3). */
export function displayMaskedCardNumber(masked: string): string {
  const local = displayServerMask(masked, 'text');
  const digits = local.replace(/[^\d۰-۹]/g, '');
  const tail = digits.slice(-4);
  if (tail.length === 0) {
    return local;
  }
  return `${MASK_CHAR.repeat(4)} ${MASK_CHAR.repeat(4)} ${MASK_CHAR.repeat(4)} ${toPersianDigits(tail)}`;
}

/** Whole-percent display for dashboard hints (e.g. ۳۳٫۳۳۳۳٪ → ۳۳٪). */
export function formatRoundedSharePercent(rate: string): string {
  const match = /^(\d+)(?:\.(\d+))?$/.exec(rate.trim());
  if (match === null) {
    return formatRatePercent(rate, { invalid: 'raw' });
  }
  const value = Number(`${match[1]}.${match[2] ?? '0'}`) * 100;
  return `${toPersianDigits(Math.round(value))}٪`;
}

export type TargetingNameLookup = {
  readonly cityName?: (id: string) => string | undefined;
  readonly organizationName?: (id: string) => string | undefined;
  readonly provinceName?: (id: string) => string | undefined;
  readonly roleName?: (id: string) => string | undefined;
};

/** Human-readable targeting audience (AC §18.9.1 impact report). */
export function targetText(target: TargetingFilter, names: TargetingNameLookup = {}): string {
  switch (target.type) {
    case 'INDIVIDUAL':
      return `${toPersianDigits(target.recipients.length)} نفر مشخص`;
    case 'ORGANIZATION': {
      const org = names.organizationName?.(target.organizationId) ?? 'سازمان';
      if (target.organizationRoleId === null || target.organizationRoleId === undefined) {
        return `اعضای فعال ${org}`;
      }
      const role = names.roleName?.(target.organizationRoleId) ?? 'نقش';
      return `${role} · ${org}`;
    }
    case 'ORGANIZATION_ROLE': {
      const role = names.roleName?.(target.organizationRoleId) ?? 'نقش سراسری';
      return `همهٔ اعضای ${role}`;
    }
    case 'GEOGRAPHY': {
      if (target.cityId === null || target.cityId === undefined) {
        const province = names.provinceName?.(target.provinceId) ?? 'استان';
        return `خریداران ${province}`;
      }
      const city = names.cityName?.(target.cityId) ?? 'شهر';
      const province = names.provinceName?.(target.provinceId);
      return province === undefined ? `خریداران ${city}` : `${city}، ${province}`;
    }
    default: {
      const _exhaustive: never = target;
      return _exhaustive;
    }
  }
}

const ENROLLMENT_ROW_ERROR_FA: Readonly<Record<string, string>> = {
  DUPLICATE_MOBILE_IN_REQUEST: 'این شماره در درخواست باز دیگری هست.',
  DUPLICATE_NATIONAL_CODE_IN_REQUEST: 'این کد ملی در درخواست باز دیگری هست.',
  INVALID_CHECKSUM: 'رقم کنترل کد ملی نادرست است.',
  INVALID_MOBILE: 'شماره موبایل معتبر نیست.',
  INVALID_NATIONAL_CODE: 'کد ملی معتبر نیست.',
  UNKNOWN_ORGANIZATION_ROLE: 'نقش سازمان شناخته نشده است.',
};

export function enrollmentValidationErrorLabel(code: string): string {
  return ENROLLMENT_ROW_ERROR_FA[code] ?? 'خطای ناشناخته';
}

export function formatLocalMobileDisplay(mobileE164: string): string {
  const local = toLocalMobile(mobileE164);
  const digits = local.replace(/\D/g, '');
  if (!/^09\d{9}$/.test(digits)) {
    return toPersianDigits(local);
  }
  return toPersianDigits(`${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`);
}
